/**
 * Circles Cloud Function service.
 * Wraps all 24 Circle-related Cloud Functions (europe-west1).
 *
 * Auth model:
 *  - ALL circle functions require authentication EXCEPT the two `/c/:token`
 *    invite endpoints (`resolveCircleInviteByTokenEurW`,
 *    `declineCircleInviteByTokenEurW`) — token possession is the capability
 *    there, exactly like `/i/:token` on rooms. They are wrapped with the same
 *    plain `httpsCallable`; the SDK simply omits the auth header when no user
 *    is signed in.
 *  - getCirclesEurW / getCircleDetailEurW are the browse/read endpoints
 *  - Mutation functions validate membership and role server-side
 */

import { httpsCallable } from 'firebase/functions';
import { FIREBASE_FUNCTIONS } from '../firebaseConfig';
import type {
  AcceptCircleInviteByTokenResponse,
  Circle,
  CircleInvitePublicView,
  CircleMember,
  CreateCircleInviteParams,
  CreateCircleInviteResponse,
  DeclineCircleInviteByTokenResponse,
  GetCirclesFilter,
  GetCirclesResponse,
  GetCircleDetailResponse,
  JoinCircleResponse,
  CreateCircleResponse,
  CircleMessagesResponse,
  RespondToCircleInviteResponse,
  RespondToCircleJoinRequestResponse,
  SentCircleMessage,
  CircleEventsResponse,
  CircleEventDetailResponse,
  CircleEvent,
} from '../types/circles';

// ─── Public endpoint (no auth required) ─────────────────────────────────────

export interface PublicCircle {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  categoryTags: string[];
  visibility: 'public';
  location: string;
  createdAt: string | null;
}

export interface GetPublicCirclesResponse {
  success: boolean;
  circles: PublicCircle[];
  hasMore: boolean;
}

const getPublicCirclesFn = httpsCallable<
  { limit?: number; startAfter?: string; categoryTag?: string },
  GetPublicCirclesResponse
>(FIREBASE_FUNCTIONS, 'getPublicCirclesEurW');

export async function getPublicCircles(
  options?: { limit?: number; startAfter?: string; categoryTag?: string }
): Promise<GetPublicCirclesResponse> {
  const result = await getPublicCirclesFn(options || {});
  return result.data;
}

// ─── Slug resolution ─────────────────────────────────────────────────────────

import { slugify } from '../utils/circleSlug';

/** Resolve a URL slug (e.g. "book-club") to a Firestore circle ID. */
export async function resolveCircleSlug(slug: string): Promise<string | null> {
  try {
    const data = await getPublicCircles({ limit: 100 });
    const match = (data.circles || []).find(
      (c) => slugify(c.name) === slug,
    );
    return match?.id ?? null;
  } catch {
    return null;
  }
}

// ─── Core Circle CRUD & Membership ──────────────────────────────────────────

const getCirclesFn = httpsCallable<
  { filter?: GetCirclesFilter; pagination?: { limit?: number; startAfter?: string } },
  GetCirclesResponse
>(FIREBASE_FUNCTIONS, 'getCirclesEurW');

const getCircleDetailFn = httpsCallable<
  { circleId: string },
  GetCircleDetailResponse
>(FIREBASE_FUNCTIONS, 'getCircleDetailEurW');

const createCircleFn = httpsCallable<
  { name: string; description: string; visibility: 'public' | 'private'; categoryTags: string[]; location?: string },
  CreateCircleResponse
>(FIREBASE_FUNCTIONS, 'createCircleEurW');

const updateCircleFn = httpsCallable<
  { circleId: string; updates: Partial<Pick<Circle, 'name' | 'description' | 'visibility' | 'categoryTags' | 'location'>> },
  { success: boolean; circle: Circle }
>(FIREBASE_FUNCTIONS, 'updateCircleEurW');

const joinCircleFn = httpsCallable<
  { circleId: string },
  JoinCircleResponse
>(FIREBASE_FUNCTIONS, 'joinCircleRequestEurW');

const leaveCircleFn = httpsCallable<
  { circleId: string },
  { success: boolean; message: string }
>(FIREBASE_FUNCTIONS, 'leaveCircleEurW');

const approveJoinFn = httpsCallable<
  { circleId: string; userId: string },
  { success: boolean; message: string }
>(FIREBASE_FUNCTIONS, 'approveJoinRequestEurW');

const rejectJoinFn = httpsCallable<
  { circleId: string; userId: string },
  { success: boolean; message: string }
>(FIREBASE_FUNCTIONS, 'rejectJoinRequestEurW');

const kickMemberFn = httpsCallable<
  { circleId: string; userId: string; reason: string },
  { success: boolean; message: string }
>(FIREBASE_FUNCTIONS, 'kickCircleMemberEurW');

const banMemberFn = httpsCallable<
  { circleId: string; userId: string; reason: string },
  { success: boolean; message: string }
>(FIREBASE_FUNCTIONS, 'banCircleMemberEurW');

const muteMemberFn = httpsCallable<
  { circleId: string; userId: string; durationHours?: number; reason?: string },
  { success: boolean; message: string }
>(FIREBASE_FUNCTIONS, 'muteCircleMemberEurW');

const archiveCircleFn = httpsCallable<
  { circleId: string },
  { success: boolean; message: string; circle: Circle }
>(FIREBASE_FUNCTIONS, 'archiveCircleEurW');

// ─── Chat ───────────────────────────────────────────────────────────────────

const sendMessageFn = httpsCallable<
  { circleId: string; messageType: string; text?: string; media?: object; replyTo?: object },
  { success: boolean; message: SentCircleMessage }
>(FIREBASE_FUNCTIONS, 'sendCircleMessageEurW');

const getMessagesFn = httpsCallable<
  // before/after are epoch millis — the backend feeds them to Timestamp.fromMillis
  { circleId: string; limit?: number; before?: number; after?: number },
  CircleMessagesResponse
>(FIREBASE_FUNCTIONS, 'getCircleMessagesEurW');

const deleteMessageFn = httpsCallable<
  { circleId: string; messageId: string },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'deleteCircleMessageEurW');

const addReactionFn = httpsCallable<
  { circleId: string; messageId: string; emoji: string },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'addCircleMessageReactionEurW');

const removeReactionFn = httpsCallable<
  { circleId: string; messageId: string; emoji: string },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'removeCircleMessageReactionEurW');

// ─── Members ────────────────────────────────────────────────────────────────

const getMembersFn = httpsCallable<
  { circleId: string },
  { success: boolean; members: CircleMember[]; totalCount: number }
>(FIREBASE_FUNCTIONS, 'getCircleMembersEurW');

// ─── Events ─────────────────────────────────────────────────────────────────

const createEventFn = httpsCallable<
  {
    circleId: string; title: string; description: string; dateTime: string;
    timezone: string; location?: string; locationType: string;
    maxAttendees?: number; recurrence?: string;
  },
  { success: boolean; event: CircleEvent }
>(FIREBASE_FUNCTIONS, 'createCircleEventEurW');

const getEventsFn = httpsCallable<
  { circleId: string; includePast?: boolean; limit?: number },
  CircleEventsResponse
>(FIREBASE_FUNCTIONS, 'getCircleEventsEurW');

const getEventDetailFn = httpsCallable<
  { circleId: string; eventId: string },
  CircleEventDetailResponse
>(FIREBASE_FUNCTIONS, 'getCircleEventDetailEurW');

const updateEventFn = httpsCallable<
  { circleId: string; eventId: string; updates: Partial<CircleEvent> },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'updateCircleEventEurW');

const cancelEventFn = httpsCallable<
  { circleId: string; eventId: string; reason?: string },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'cancelCircleEventEurW');

const rsvpEventFn = httpsCallable<
  { circleId: string; eventId: string; status: string },
  { success: boolean }
>(FIREBASE_FUNCTIONS, 'rsvpCircleEventEurW');

const withdrawRsvpFn = httpsCallable<
  { circleId: string; eventId: string },
  { success: boolean; message: string }
>(FIREBASE_FUNCTIONS, 'withdrawCircleEventEurW');

// ═══════════════════════════════════════════════════════════════════════════
// Exported service functions
// ═══════════════════════════════════════════════════════════════════════════

// ─── Browse / Detail ────────────────────────────────────────────────────────

export async function getCircles(
  filter?: GetCirclesFilter,
  pagination?: { limit?: number; startAfter?: string }
): Promise<GetCirclesResponse> {
  const result = await getCirclesFn({ filter, pagination });
  return result.data;
}

export async function getMyCircles(): Promise<GetCirclesResponse> {
  return getCircles({ membershipStatus: 'member' });
}

export async function getCircleDetail(circleId: string): Promise<GetCircleDetailResponse> {
  const result = await getCircleDetailFn({ circleId });
  return result.data;
}

// ─── Create / Update / Archive ──────────────────────────────────────────────

export interface CreateCircleParams {
  name: string;
  description: string;
  visibility: 'public' | 'private';
  categoryTags: string[];
  location?: string;
}

export async function createCircle(params: CreateCircleParams): Promise<CreateCircleResponse> {
  const result = await createCircleFn(params);
  return result.data;
}

export async function updateCircle(
  circleId: string,
  // visibility accepted for callers that resend the full form; updateCircleEurW
  // only persists its allowlisted fields and drops the rest server-side
  updates: Partial<Pick<Circle, 'name' | 'description' | 'visibility' | 'categoryTags' | 'location'>>
): Promise<{ success: boolean; circle: Circle }> {
  const result = await updateCircleFn({ circleId, updates });
  return result.data;
}

export async function archiveCircle(circleId: string): Promise<{ success: boolean; message: string }> {
  const result = await archiveCircleFn({ circleId });
  return result.data;
}

// ─── Membership ─────────────────────────────────────────────────────────────

export async function joinCircle(circleId: string): Promise<JoinCircleResponse> {
  const result = await joinCircleFn({ circleId });
  return result.data;
}

export async function leaveCircle(circleId: string): Promise<{ success: boolean; message: string }> {
  const result = await leaveCircleFn({ circleId });
  return result.data;
}

export async function approveJoinRequest(circleId: string, userId: string) {
  const result = await approveJoinFn({ circleId, userId });
  return result.data;
}

export async function rejectJoinRequest(circleId: string, userId: string) {
  const result = await rejectJoinFn({ circleId, userId });
  return result.data;
}

// ─── Moderation ─────────────────────────────────────────────────────────────

export async function kickMember(circleId: string, userId: string, reason: string) {
  const result = await kickMemberFn({ circleId, userId, reason });
  return result.data;
}

export async function banMember(circleId: string, userId: string, reason: string) {
  const result = await banMemberFn({ circleId, userId, reason });
  return result.data;
}

export async function muteMember(circleId: string, userId: string, durationHours = 24, reason?: string) {
  const result = await muteMemberFn({ circleId, userId, durationHours, reason });
  return result.data;
}

// ─── Members ─────────────────────────────────────────────────────────────────

export async function getCircleMembers(
  circleId: string
): Promise<{ success: boolean; members: CircleMember[]; totalCount: number }> {
  const result = await getMembersFn({ circleId });
  return result.data;
}

// ─── Chat ───────────────────────────────────────────────────────────────────

export async function sendCircleMessage(
  circleId: string,
  text: string,
  replyTo?: { messageId: string; text: string; userId: string }
): Promise<{ success: boolean; message: SentCircleMessage }> {
  const result = await sendMessageFn({
    circleId,
    messageType: 'text',
    text,
    replyTo,
  });
  return result.data;
}

export async function getCircleMessages(
  circleId: string,
  options?: { limit?: number; before?: number; after?: number }
): Promise<CircleMessagesResponse> {
  const result = await getMessagesFn({
    circleId,
    limit: options?.limit ?? 50,
    before: options?.before,
    after: options?.after,
  });
  return result.data;
}

export async function deleteCircleMessage(circleId: string, messageId: string) {
  const result = await deleteMessageFn({ circleId, messageId });
  return result.data;
}

export async function addMessageReaction(circleId: string, messageId: string, emoji: string) {
  const result = await addReactionFn({ circleId, messageId, emoji });
  return result.data;
}

export async function removeMessageReaction(circleId: string, messageId: string, emoji: string) {
  const result = await removeReactionFn({ circleId, messageId, emoji });
  return result.data;
}

// ─── Events ─────────────────────────────────────────────────────────────────

export interface CreateEventParams {
  circleId: string;
  title: string;
  description: string;
  dateTime: string; // ISO string
  timezone: string;
  location?: string;
  locationType: 'physical' | 'virtual' | 'tbd';
  maxAttendees?: number;
  recurrence?: 'none' | 'daily' | 'weekly' | 'monthly';
}

export async function createCircleEvent(params: CreateEventParams) {
  const result = await createEventFn(params);
  return result.data;
}

export async function getCircleEvents(
  circleId: string,
  includePast = false,
  limit = 50
): Promise<CircleEventsResponse> {
  const result = await getEventsFn({ circleId, includePast, limit });
  return result.data;
}

export async function getCircleEventDetail(
  circleId: string,
  eventId: string
): Promise<CircleEventDetailResponse> {
  const result = await getEventDetailFn({ circleId, eventId });
  return result.data;
}

export async function updateCircleEvent(
  circleId: string,
  eventId: string,
  updates: Partial<CircleEvent>
) {
  const result = await updateEventFn({ circleId, eventId, updates });
  return result.data;
}

export async function cancelCircleEvent(circleId: string, eventId: string, reason?: string) {
  const result = await cancelEventFn({ circleId, eventId, reason });
  return result.data;
}

export async function rsvpToEvent(circleId: string, eventId: string, status: 'going' | 'maybe' | 'notGoing') {
  const result = await rsvpEventFn({ circleId, eventId, status });
  return result.data;
}

export async function withdrawFromEvent(circleId: string, eventId: string) {
  const result = await withdrawRsvpFn({ circleId, eventId });
  return result.data;
}

// ═══════════════════════════════════════════════════════════════════════════
// Circle invites (PPL-213 backend / PPL-215 web)
// ═══════════════════════════════════════════════════════════════════════════
//
// Mirrors roomService's mediation-invite wrappers: typed callable refs here,
// thin exported async functions below. Circles are the PERSONAL identity half
// — anything these return that names a person is a firstName, never a
// handle-only anonymous label.

const createCircleInviteFn = httpsCallable<
  CreateCircleInviteParams,
  CreateCircleInviteResponse
>(FIREBASE_FUNCTIONS, 'createCircleInviteEurW');

const respondToCircleInviteFn = httpsCallable<
  { inviteId: string; response: 'accept' | 'decline' },
  RespondToCircleInviteResponse
>(FIREBASE_FUNCTIONS, 'respondToCircleInviteEurW');

/**
 * UNAUTHENTICATED — the public `/c/:token` landing resolver. Auth context is
 * still forwarded when a user happens to be signed in, which is how the server
 * computes `viewerRole` (inviter / invitee / stranger).
 */
const resolveCircleInviteByTokenFn = httpsCallable<
  { token: string },
  CircleInvitePublicView
>(FIREBASE_FUNCTIONS, 'resolveCircleInviteByTokenEurW');

const acceptCircleInviteByTokenFn = httpsCallable<
  { token: string },
  AcceptCircleInviteByTokenResponse
>(FIREBASE_FUNCTIONS, 'acceptCircleInviteByTokenEurW');

/** UNAUTHENTICATED (like the resolver) — saying no never requires a signup. */
const declineCircleInviteByTokenFn = httpsCallable<
  { token: string },
  DeclineCircleInviteByTokenResponse
>(FIREBASE_FUNCTIONS, 'declineCircleInviteByTokenEurW');

const respondToCircleJoinRequestFn = httpsCallable<
  { circleId: string; userId: string; decision: 'approve' | 'reject' },
  RespondToCircleJoinRequestResponse
>(FIREBASE_FUNCTIONS, 'respondToCircleJoinRequestEurW');

/**
 * Admin-only. Pass `inviteeId` for an on-pplkpr invitee, `inviteeEmail` for
 * someone off-platform (the server re-resolves an email that belongs to an
 * existing user), or `channel:'link'` with neither to mint a shareable URL.
 */
export async function createCircleInvite(
  params: CreateCircleInviteParams,
): Promise<CreateCircleInviteResponse> {
  const result = await createCircleInviteFn(params);
  return result.data;
}

/** In-app accept/decline off the notification rail entry (carries inviteId). */
export async function respondToCircleInvite(
  inviteId: string,
  response: 'accept' | 'decline',
): Promise<RespondToCircleInviteResponse> {
  const result = await respondToCircleInviteFn({ inviteId, response });
  return result.data;
}

export async function resolveCircleInviteByToken(
  token: string,
): Promise<CircleInvitePublicView> {
  const result = await resolveCircleInviteByTokenFn({ token });
  return result.data;
}

export async function acceptCircleInviteByToken(
  token: string,
): Promise<AcceptCircleInviteByTokenResponse> {
  const result = await acceptCircleInviteByTokenFn({ token });
  return result.data;
}

export async function declineCircleInviteByToken(
  token: string,
): Promise<DeclineCircleInviteByTokenResponse> {
  const result = await declineCircleInviteByTokenFn({ token });
  return result.data;
}

/**
 * Admin approve/reject of a pending join request, checked and applied inside
 * ONE transaction (and it notifies the requester). Supersedes the older
 * approveJoinRequest / rejectJoinRequest pair, which is kept above only for
 * already-deployed clients.
 */
export async function respondToCircleJoinRequest(
  circleId: string,
  userId: string,
  decision: 'approve' | 'reject',
): Promise<RespondToCircleJoinRequestResponse> {
  const result = await respondToCircleJoinRequestFn({ circleId, userId, decision });
  return result.data;
}
